import useSWR from "swr";
import { Link } from "react-router-dom";
import { AlertTriangle, Car, Gauge, RadioTower } from "lucide-react";
import { fetcher } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { INCIDENT, STATUS, fmtK, pct } from "@/lib/traffic";
import { StripMap, StatusPill } from "@/components/traffic/StripMap";
import { Kpi, LivePing, BacklogBadge, RebuildBanner } from "@/components/traffic/Widgets";
import { RadarNotesStrip } from "@/components/traffic/RadarNotes";
import { FeedPulse } from "@/components/traffic/FeedPulse";
import { useLive } from "@/hooks/useLive";
import { cn } from "@/lib/utils";

function SiteTile({ s }) {
  const dirs = ["NB", "SB"].filter((d) => !s.side || s.side === d);
  const veh = dirs.reduce((a, d) => a + (s.dirs?.[d]?.n || 0), 0);
  const speed = (d) => (s.offline || !s.dirs?.[d]?.n ? "—" : s.dirs[d].spd);
  return (
    <Link to={`/live?site=${s.site_id}`} data-testid={`overview-site-${s.site_id}`}
      className={cn("flex flex-col gap-0.5 rounded-2xl border border-border/60 px-3 py-1.5 transition-colors hover:bg-secondary/60", s.offline && "border-dashed opacity-75")}>
      <div className="flex items-center justify-between gap-2">
        <div className="truncate text-sm font-semibold" title={s.name}>{s.name}</div>
        <StatusPill status={s.offline ? "offline" : s.status} testId={`overview-site-status-${s.site_id}`} />
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="num min-w-0 truncate text-[11px] text-muted-foreground" title={s.offline ? s.offline_reason : s.ip}>
          {s.site_id} · {s.offline ? s.offline_reason : `${s.ip} · cycle ${s.last_ts?.slice(11, 16) || "—"}`}
        </div>
        <div className="num shrink-0 text-right leading-none">
          {s.side
            ? <span className="text-lg font-semibold">{speed(s.side)}<span className="ml-0.5 text-[10px] font-normal text-muted-foreground">km/h</span></span>
            : <span className="text-sm font-semibold">NB {speed("NB")} · SB {speed("SB")}<span className="ml-0.5 text-[10px] font-normal text-muted-foreground">km/h</span></span>}
          <div className="mt-0.5 text-[10px] text-muted-foreground">{s.offline ? "" : `${veh} veh / 5 min`}</div>
        </div>
      </div>
    </Link>
  );
}

// Control-room layout for a 16:9 wall display (1920×1080): header with the live data pulse, KPIs, full-width strip map,
// then every radar site as a tile — no scrolling.
export default function Overview() {
  const { data: ov } = useSWR("/traffic/overview", fetcher, { refreshInterval: 10000 });
  const { live } = useLive();
  const k = ov?.kpi;
  const delta = k ? pct(k.vehicles_today, k.vehicles_yesterday_same_time) : null;
  const statuses = ov?.statuses || {};
  const radarSub = k ? (k.radars_offline === 0 ? "All radars online and detecting" : `${k.radars_offline} offline · ${k.radars_no_ping} no ping reply · ${k.radars_no_detections} no vehicle data`) : null;
  return (
    <div className="flex flex-col gap-3" data-testid="overview-page">
      <RebuildBanner rebuild={ov?.rebuild} />
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="shrink-0">
          <div className="eyebrow">Lebuhraya Pantai Timur 2 · KM258 – KM424</div>
          <h1 className="mt-0.5 text-2xl font-semibold tracking-tight sm:text-3xl">Corridor overview</h1>
        </div>
        <FeedPulse feed={live?.feed} />
        <div className="flex shrink-0 flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <BacklogBadge feed={live?.feed} />
            <LivePing online={live?.feed?.online} label={live?.feed?.online ? `LIVE · radar server ${live.feed.age_s}s ago` : "Radar server not reporting"} />
          </div>
          <span className="num text-xs text-muted-foreground" data-testid="overview-clock">{ov?.now?.slice(11, 16)} MYT{live?.feed?.version ? ` · sender ${live.feed.version}` : ""}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Vehicles today" value={fmtK(k?.vehicles_today)} icon={Car} testId="kpi-card-vehicles-today" compact
          sub={delta == null ? "vs. yesterday at this time: —" : `${delta >= 0 ? "+" : ""}${delta}% vs. yesterday at this time`} tone={delta == null ? null : delta >= 0 ? "up" : "down"} />
        <Kpi label="Average speed" value={k ? `${k.avg_speed} km/h` : "—"} icon={Gauge} testId="kpi-card-avg-speed" compact
          sub={k ? `S ${fmtK(k.classes.small)} · M ${fmtK(k.classes.medium)} · L ${fmtK(k.classes.large)}` : null} />
        <Kpi label="Incidents today" value={fmtNum(k?.incidents_today)} icon={AlertTriangle} testId="kpi-card-incidents" compact
          sub={k ? Object.entries(k.incidents_by_kind).map(([kk, v]) => `${INCIDENT[kk]?.label || kk} ${v}`).join(" · ") || "No incidents so far" : null} />
        <Kpi label="Radars online" value={k ? `${k.radars_online} / ${k.radars_total}` : "—"} icon={RadioTower} testId="kpi-card-radars" compact
          sub={radarSub} tone={k && k.radars_online < k.radars_total ? "down" : null} />
      </div>
      <RadarNotesStrip notes={ov?.radar_notes} sites={ov?.sites} />

      <section className="surface p-4 2xl:p-5" data-testid="overview-strip-map">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold md:text-lg">Live status of all 20 radar sites</h2>
            <p className="text-xs text-muted-foreground">Latest 5-minute cycle per radar · thresholds {live?.thresholds?.smooth_min_kmh}/{live?.thresholds?.slow_min_kmh} km/h</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["smooth", "quiet", "slow", "congested", "offline"].map((s) => statuses[s] ? <StatusPill key={s} status={s} label={`${statuses[s]} ${STATUS[s].label}`} testId={`status-count-${s}`} /> : null)}
          </div>
        </div>
        <StripMap sites={ov?.sites} large />
      </section>

      <section className="surface p-4 2xl:p-5" data-testid="overview-site-table">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-base font-semibold md:text-lg">Sites at a glance</h2>
          <Link to="/live" className="text-xs text-primary hover:underline" data-testid="overview-open-live">Open live view →</Link>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5">
          {(ov?.sites || []).map((s) => <SiteTile key={s.site_id} s={s} />)}
        </div>
      </section>
    </div>
  );
}
